import * as XLSX from 'xlsx';

import { approvalState, APPROVED, REJECTED } from './employeeApproval';
import { isBankApproved } from './employeeBankApproval';
import { getLocationName, getCostCenter } from './employeeUtils';


const fmt = (v) => (v === null || v === undefined || v === '' || v === 'null' || v === 'undefined')
  ? ''
  : String(v).trim();

const fld = (emp, newKey, oldKey) => fmt(emp[newKey]) || fmt(emp[oldKey]);

const pad2 = (n) => String(n).padStart(2, '0');

function stamp(d = new Date()) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())} ` +
         `${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
}

export function excelDate(v) {
  const raw = fmt(v);
  if (!raw) return '';

  const iso = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (iso) return `${iso[1]}-${pad2(iso[2])}-${pad2(iso[3])}`;

  // The API sends M/D/YYYY ("10/14/1996 12:00:00 AM"). Only when the first
  // part can't be a month (> 12) is it read as D/M/YYYY.
  const mdy = raw.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})/);
  if (mdy) {
    const [a, b] = [Number(mdy[1]), Number(mdy[2])];
    return a > 12
      ? `${mdy[3]}-${pad2(b)}-${pad2(a)}`
      : `${mdy[3]}-${pad2(a)}-${pad2(b)}`;
  }

  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/* ── Decoding stored codes ──────────────────────────────────────────────── */

const GENDER_MAP   = { M: 'Male', F: 'Female' };
const MARITAL_MAP  = { S: 'Single', M: 'Married', D: 'Divorced', W: 'Widowed' };
const RELIGION_MAP = {
  B: 'Buddhism', C: 'Christian', H: 'Hindu', I: 'Islam', O: 'Other',
  1: 'Buddhism', 2: 'Christian', 3: 'Hindu', 4: 'Islam', 5: 'Other',
};
const BLOOD_MAP = {
  1: 'A+', 2: 'A-', 3: 'B+', 4: 'B-', 5: 'AB+', 6: 'AB-', 7: 'O+', 8: 'O-',
  0: 'Group O', 9: 'Group B', 10: 'Group B+', 11: 'Group B-', 12: 'Unknown',
};

const dec = (map, v) => {
  const raw = fmt(v);
  return raw ? (map[raw] || raw) : '';
};

/** "True" / "False" arrive as strings, and "False" is truthy in JavaScript. */
const truthy = (v) => v === true || v === 1 ||
  ['true', '1', 'yes', 'y'].includes(String(v ?? '').trim().toLowerCase());

export function isActive(emp) {
  if (emp.HED_IS_ACTIVE !== undefined && emp.HED_IS_ACTIVE !== null && emp.HED_IS_ACTIVE !== '') {
    return truthy(emp.HED_IS_ACTIVE);
  }
  return !emp.p_inactive_type || emp.p_inactive_type === '' || emp.p_inactive_type === '0';
}

const yesNo = (v) => {
  const raw = fmt(v);
  if (!raw) return '';
  return truthy(v) ? 'Yes' : 'No';
};

const EMPLOYEE_TYPE_MAP = { Field: 'Field Staff', Staff: 'Office Staff' };
// The forms always save nationality as 1.
const NATIONALITY_MAP = { 1: 'Sri Lankan' };

const approvalLabel = (emp) => {
  const state = approvalState(emp);
  if (state === APPROVED) return 'Approved';
  if (state === REJECTED) return 'Rejected';
  return 'Pending';
};

/* ── Nested records ──────────────────────────────────────────────────────── */

const listOf = (emp, ...keys) => {
  for (const k of keys) if (Array.isArray(emp[k])) return emp[k];
  return [];
};

const banksOf     = (e) => listOf(e, 'Banks', '_Banks');
const addressesOf = (e) => listOf(e, 'Addresses', '_Addresses');
const contactsOf  = (e) => listOf(e, 'Contacts', '_Contacts');
const qualsOf     = (e) => listOf(e, 'Qualifications', '_Qualifications', 'Educations', '_Educations');

/** The account marked primary, else the first one on file. */
const primaryBank = (e) => {
  const banks = banksOf(e);
  return banks.find((b) => truthy(b.HEB_IS_PRIMARY)) || banks[0] || {};
};

const addressOfType = (e, type) =>
  addressesOf(e).find((a) => fmt(a.HEA_ADDRESS_TYPE).toUpperCase() === type) || {};

/* ── Region ── */
// The address stores IDs / codes; the master lists (ctx) give the names.
const REGION = {
  province: {
    keys: ['HEA_PROVINCE_ID', 'HEA_PROVINCE_CODE', 'PROVINCE_CODE'],
    list: 'provinces',
    codeKeys: ['PROVINCE_CODE', 'ProvinceCode', 'provinceCode', 'HPD_PROVINCE_CODE', 'Code', 'code', 'Id', 'id'],
    nameKeys: ['PROVINCE_NAME', 'ProvinceName', 'provinceName', 'HPD_PROVINCE_NAME', 'Name', 'name'],
  },
  district: {
    keys: ['HEA_DISTRICT_ID', 'HEA_DISTRICT_CODE', 'DISTRICT_CODE'],
    list: 'districts',
    codeKeys: ['DISTRICT_CODE', 'DistrictCode', 'districtCode', 'HDD_DISTRICT_CODE', 'Code', 'code', 'Id', 'id'],
    nameKeys: ['DISTRICT_NAME', 'DistrictName', 'districtName', 'HDD_DISTRICT_NAME', 'Name', 'name'],
  },
  electorate: {
    keys: ['HEA_ELECTORATE_ID', 'HEA_ELECTORATE_CODE', 'ELECTORATE_CODE'],
    list: 'electorates',
    codeKeys: ['ELECTORATE_CODE', 'ElectorateCode', 'electorateCode', 'Code', 'code', 'Id', 'id'],
    nameKeys: ['ELECTORATE_AREA', 'ElectorateArea', 'electorateArea', 'Name', 'name'],
  },
  policeStation: {
    keys: ['HEA_POLICE_STATION_ID', 'HEA_STATION_CODE', 'HEA_STAION_CODE', 'HPD_STATION_CODE'],
    list: 'policeStations',
    codeKeys: ['HPD_STATION_CODE', 'StationCode', 'stationCode', 'Code', 'code', 'Id', 'id'],
    nameKeys: ['HPD_POLICESTATION_NAME', 'PoliceStationName', 'policeStationName', 'Name', 'name'],
  },
  grama: {
    keys: ['HEA_GRAMASEWAKA_CODE', 'HEA_GRAMA_CODE'],
    list: 'gramas',
    codeKeys: ['GRAMASEWAKA_CODE', 'GramasewakaCode', 'gramasewakaCode', 'HGD_GRAMASEWAKA_CODE',
      'HGD_GRAMASEWAKADIVISION_CODE', 'Code', 'code', 'Id', 'id'],
    nameKeys: ['GRAMASEWAKA_DIVISION_NAME', 'GramasewakaDivisionName', 'gramasewakaDivisionName',
      'HGD_GRAMASEWAKA_DIVISION_NAME', 'HGD_GRAMASEWAKADIVISION_NAME', 'DIVISION_NAME', 'Name', 'name'],
  },
};

const REGION_ADDRESS_KEYS = Object.values(REGION).flatMap((r) => r.keys);

/** Looks a code up in one of the ctx lists and returns its label, else the code. */
const regionName = (code, rows, codeKeys, nameKeys) => {
  if (!code) return '';
  const hit = (rows || []).find((r) => codeKeys.some((k) => fmt(r[k]) === String(code)));
  if (!hit) return String(code);
  for (const k of nameKeys) if (fmt(hit[k])) return fmt(hit[k]);
  return String(code);
};

/** Name of one region field on an address record. */
const regionOf = (address, which, ctx) => {
  const spec = REGION[which];
  const a = address || {};
  let code = '';
  for (const k of spec.keys) if (fmt(a[k])) { code = fmt(a[k]); break; }
  return regionName(code, ctx && ctx[spec.list], spec.codeKeys, spec.nameKeys);
};

/** "No. 25, Galle Road, Colombo 10350" from whichever lines are filled. */
const addressLine = (a) => {
  const street = [a.HEA_ADDRESS_LINE1, a.HEA_ADDRESS_LINE2, a.HEA_ADDRESS_LINE3]
    .map(fmt).filter(Boolean).join(', ');
  const tail = [fmt(a.HEA_CITY), fmt(a.HEA_POSTAL_CODE)].filter(Boolean).join(' ');
  return [street, tail].filter(Boolean).join(', ');
};


function reportingOfficer(emp, all = []) {
  const id = fmt(emp.HED_REPORTING_EMPLOYEE_ID);
  const onRow = fmt(emp.HED_REPORTING_EMPLOYEE_NAME);
  if (!id) return onRow;

  const match = all.find((r) => fmt(r.HED_EMPLOYEE_ID) === id);
  if (!match) return onRow || id;

  const name = fmt(match.HED_REPORT_NAME) ||
    [fmt(match.HED_FIRST_NAME), fmt(match.HED_LAST_NAME)].filter(Boolean).join(' ') || id;
  const svcNo = fmt(match.HED_SERVICE_NO);
  return svcNo ? `${name} (${svcNo})` : name;
}


export const EMPLOYEE_EXPORT_COLUMNS = [
  // Identification
  { label: 'Service No',        width: 13, get: (e) => fld(e, 'HED_SERVICE_NO', 'p_ced_service_no') },
  { label: 'Abbreviation',      width: 12, get: (e) => fmt(e.HED_ABBRIVATION) },
  { label: 'EPF No',            width: 12, get: (e) => fld(e, 'HED_EPF_NO', 'p_epf') },
  { label: 'ETF No',            width: 12, get: (e) => fmt(e.HED_ETF_NO) },
  { label: 'TIN No',            width: 14, get: (e) => fld(e, 'HED_TIN_NO', 'p_TinNo') },
  { label: 'NIC No',            width: 16, get: (e) => fld(e, 'HED_NIC_NO', 'p_nid') },
  { label: 'Passport No',       width: 14, get: (e) => fld(e, 'HED_PASSPORT_NO', 'p_passport') },

  // Name
  { label: 'Title',             width: 8,  get: (e) => fld(e, 'HED_TITLE', 'p_title') },
  { label: 'First Name',        width: 18, get: (e) => fld(e, 'HED_FIRST_NAME', 'p_first_name') },
  { label: 'Middle Name',       width: 18, get: (e) => fmt(e.HED_MIDDLE_NAME) },
  { label: 'Last Name',         width: 18, get: (e) => fld(e, 'HED_LAST_NAME', 'p_last_name') },
  { label: 'Initials',          width: 12, get: (e) => fld(e, 'HED_INITIALS', 'p_initials') },
  { label: 'Report Name',       width: 28, get: (e) => fld(e, 'HED_REPORT_NAME', 'p_report_name') },

  // Personal
  { label: 'Gender',            width: 10, get: (e) => dec(GENDER_MAP, fld(e, 'HED_GENDER', 'p_sex')) },
  { label: 'Date of Birth',     width: 13, get: (e) => excelDate(fld(e, 'HED_DATE_OF_BIRTH', 'p_birth_date')) },
  { label: 'Marital Status',    width: 14, get: (e) => dec(MARITAL_MAP, fld(e, 'HED_MARITAL_STATUS', 'p_marital_status')) },
  { label: 'Religion',          width: 12, get: (e) => dec(RELIGION_MAP, fld(e, 'HED_RELIGION', 'p_religion')) },
  { label: 'Blood Group',       width: 12, get: (e) => dec(BLOOD_MAP, fld(e, 'HED_BLOOD_GROUP', 'p_blood_group')) },
  { label: 'Nationality',       width: 12, get: (e) => dec(NATIONALITY_MAP, e.HED_NATIONALITY) },
  { label: 'No. of Children',   width: 13, get: (e) => fmt(e.HED_NO_OF_CHILDREN) },

  // Contact
  { label: 'Mobile No',         width: 15, get: (e) => fld(e, 'HED_MOBILE_NO', 'p_mobile') },
  { label: 'Home No',           width: 15, get: (e) => fld(e, 'HED_HOME_NO', 'p_home_tel') },
  { label: 'Office No',         width: 15, get: (e) => fld(e, 'HED_OFFICE_NO', 'p_office_tel') },
  { label: 'Personal Email',    width: 28, get: (e) => fld(e, 'HED_PERSONAL_EMAIL', 'p_personal_email') },
  { label: 'Company Email',     width: 28, get: (e) => fld(e, 'HED_COMPANY_EMAIL', 'p_email_address') },

  // Addresses — as one line each; the Addresses sheet has them split up
  { label: 'Permanent Address', width: 40, get: (e) => addressLine(addressOfType(e, 'P')) },
  { label: 'Current Address',   width: 40, get: (e) => addressLine(addressOfType(e, 'C')) },
  { label: 'Distance (km)',     width: 13, get: (e) => fld(e, 'HED_DISTANCE', 'p_Distance') },

  // Region — from the permanent address, shown by name
  { label: 'Province',       width: 18, get: (e, ctx) => regionOf(addressOfType(e, 'P'), 'province', ctx) },
  { label: 'District',       width: 18, get: (e, ctx) => regionOf(addressOfType(e, 'P'), 'district', ctx) },
  { label: 'Electorate',     width: 20, get: (e, ctx) => regionOf(addressOfType(e, 'P'), 'electorate', ctx) },
  { label: 'Police Station', width: 22, get: (e, ctx) => regionOf(addressOfType(e, 'P'), 'policeStation', ctx) },
  { label: 'Grama Division', width: 22, get: (e, ctx) => regionOf(addressOfType(e, 'P'), 'grama', ctx) },

  // Employment
  { label: 'Designation Code',  width: 14, get: (e) => fmt(e.HED_DESIGNATION_CODE) },
  { label: 'Designation',       width: 24, get: (e) => fld(e, 'HED_DESIGNATION_NAME', 'p_designation') },
  { label: 'Department Code',   width: 14, get: (e) => fmt(e.HDD_DEPARTMENT_CODE) },
  { label: 'Department',        width: 24, get: (e) => fld(e, 'HDD_DEPARTMENT_NAME', 'p_department_code') },
  { label: 'Category Code',     width: 12, get: (e) => fmt(e.HEC_CATEGORY_CODE) },
  { label: 'Employment Category', width: 18, get: (e) => fld(e, 'HEC_CATEGORY_NAME', 'p_cat_code') },
  { label: 'Employee Type',     width: 14, get: (e) => dec(EMPLOYEE_TYPE_MAP, e.HED_EMPLOYEE_TYPE) },
  { label: 'OT Type',           width: 12, get: (e) => fmt(e.HED_OT_TYPE) },
  { label: 'Grade',             width: 16, get: (e) => fmt(e.HGD_GRADE_NAME) },
  { label: 'Grade Code',        width: 12, get: (e) => fmt(e.HGD_GRADE_CODE) },
  { label: 'Grade Min Salary',  width: 14, get: (e) => fmt(e.HGD_MIN_SALARY) },
  { label: 'Grade Max Salary',  width: 14, get: (e) => fmt(e.HGD_MAX_SALARY) },
  // Two separate facts, so two columns — the old single column showed the
  // cost centre under a heading that promised both.
  { label: 'Location',          width: 22, get: (e) => getLocationName(e) },
  { label: 'Cost Center',       width: 18, get: (e) => getCostCenter(e) },
  { label: 'Group',             width: 14, get: (e) => fmt(e.HED_GROUP) },
  { label: 'Reporting Officer', width: 26, get: (e, ctx) => reportingOfficer(e, ctx.allEmployees) },
  { label: 'Shift Code',        width: 11, get: (e) => fmt(e.HSD_SHIFT_CODE) },
  { label: 'Shift',             width: 16, get: (e) => fmt(e.HSD_SHIFT_NAME) },
  { label: 'Shift Start',       width: 11, get: (e) => fmt(e.HSD_START_TIME) },
  { label: 'Shift End',         width: 11, get: (e) => fmt(e.HSD_END_TIME) },
  { label: 'Break Start',       width: 11, get: (e) => fmt(e.HSD_BREAK_START_TIME) },
  { label: 'Break End',         width: 11, get: (e) => fmt(e.HSD_BREAK_END_TIME) },
  { label: 'Break (min)',       width: 11, get: (e) => fmt(e.HSD_BREAK_DURATION_MIN) },
  { label: 'Grace In (min)',    width: 13, get: (e) => fmt(e.HSD_GRACE_IN_MIN) },
  { label: 'Grace Out (min)',   width: 13, get: (e) => fmt(e.HSD_GRACE_OUT_MIN) },
  { label: 'Half Day Hours',    width: 13, get: (e) => fmt(e.HSD_HALF_DAY_HOURS) },
  { label: 'Full Day Hours',    width: 13, get: (e) => fmt(e.HSD_FULL_DAY_HOURS) },
  { label: 'Night Shift',       width: 11, get: (e) => yesNo(e.HSD_IS_NIGHT_SHIFT) },
  { label: 'Flexible Shift',    width: 13, get: (e) => yesNo(e.HSD_IS_FLEXIBLE_SHIFT) },
  { label: 'OT After (min)',    width: 13, get: (e) => fmt(e.HSD_OT_AFTER_MINUTES) },
  { label: 'Join Date',         width: 13, get: (e) => excelDate(fld(e, 'HED_DATE_OF_JOINING', 'p_recruitment_date')) },
  { label: 'Recruitment Date',  width: 15, get: (e) => excelDate(e.HED_RECRUITMENT_DATE) },
  { label: 'Confirmation Date', width: 15, get: (e) => excelDate(fld(e, 'HED_CONFIRMATION_DATE', 'p_permanent_date')) },
  { label: 'Permanent Date',    width: 14, get: (e) => excelDate(e.HED_PERMANENT_DATE) },
  { label: 'Promoted Date',     width: 14, get: (e) => excelDate(e.HED_PROMOTED_DATE) },
  { label: 'Retirement Date',   width: 14, get: (e) => excelDate(e.HED_RETIREMENT_DATE) },
  { label: 'Termination Date',  width: 15, get: (e) => excelDate(e.HED_TERMINATION_DATE) },
  { label: 'Total Experience',  width: 14, get: (e) => fmt(e.HED_TOTAL_EXPERIANCE) || fmt(e.HED_TOTAL_EXPERIENCE) },
  { label: 'Notice Period (days)', width: 16, get: (e) => fmt(e.HED_NOTICE_PERIOD_DAYS) },
  { label: 'OT Rate',           width: 11, get: (e) => fmt(e.HED_OT_RATE) },

  // Status
  { label: 'Status',            width: 10, get: (e) => (isActive(e) ? 'Active' : 'Inactive') },
  { label: 'Leaving Date',      width: 13, get: (e) => (isActive(e) ? '' : excelDate(e.HED_LEAVING_DATE)) },
  { label: 'Leaving Reason',    width: 22, get: (e) => (isActive(e) ? '' : fmt(e.HED_LEAVING_REASON)) },
  { label: 'Leaving Remarks',   width: 22, get: (e) => (isActive(e) ? '' : fmt(e.HED_LEAVING_REMARKS)) },
  { label: 'Approval Status',   width: 14, get: (e) => approvalLabel(e) },
  { label: 'Approved By',       width: 14, get: (e) => fmt(e.HED_APPROVED_BY) },
  { label: 'Approved Date',     width: 14, get: (e) => excelDate(e.HED_APPROVED_DATE) },
  { label: 'System User',       width: 12, get: (e) => (truthy(e.HED_IS_SYSTEM_USER) ? 'Yes' : 'No') },

  // Primary bank — every account is on the Bank Accounts sheet
  { label: 'Bank',              width: 22, get: (e) => fmt(primaryBank(e).HBD_BANK_NAME) },
  { label: 'Bank Branch',       width: 22, get: (e) => fmt(primaryBank(e).HBB_BRANCH_NAME) },
  { label: 'Account Number',    width: 20, get: (e) => fmt(primaryBank(e).HEB_ACCOUNT_NUMBER) },
  { label: 'Account Name',      width: 20, get: (e) => fmt(primaryBank(e).HEB_ACCOUNT_NAME) },
  { label: 'Account Type',      width: 13, get: (e) => fmt(primaryBank(e).HEB_ACCOUNT_TYPE) },
  { label: 'Bank Approved',     width: 13, get: (e) => {
    const banks = banksOf(e);
    if (!banks.length) return '';
    return banks.every(isBankApproved) ? 'Yes' : 'No';
  } },
  { label: 'Bank Accounts',     width: 13, get: (e) => String(banksOf(e).length) },

  // First emergency contact — every contact is on its own sheet
  { label: 'Emergency Contact', width: 22, get: (e) => fmt((contactsOf(e)[0] || {}).HEC_CONTACT_NAME) },
  { label: 'Emergency Relationship', width: 18, get: (e) => fmt((contactsOf(e)[0] || {}).HEC_RELATIONSHIP) },
  { label: 'Emergency Contact No', width: 18, get: (e) => fmt((contactsOf(e)[0] || {}).HEC_CONTACT_NUMBER) },

  { label: 'Qualifications',    width: 13, get: (e) => String(qualsOf(e).length) },
  { label: 'Documents',         width: 11, get: (e) => String(listOf(e, 'Documents', '_Documents').length) },
  { label: 'Remarks',           width: 30, get: (e) => fld(e, 'HED_REMARKS', 'p_remarks') },
];

/* ── Everything else GetEmployees returns ────────────────────────────────── */

// Keys the columns above already show (including the alternate names they
// fall back to). Every other plain field on the record is added after them,
// so a field the API returns is never left out of the Employees sheet.
const COVERED_KEYS = new Set([
  'HED_SERVICE_NO', 'p_ced_service_no', 'HED_EMPLOYEE_ID', 'HED_ABBRIVATION',
  'HED_EPF_NO', 'p_epf', 'HED_ETF_NO', 'HED_TIN_NO', 'p_TinNo', 'HED_NIC_NO', 'p_nid',
  'HED_PASSPORT_NO', 'p_passport', 'HED_TITLE', 'p_title', 'HED_FIRST_NAME', 'p_first_name',
  'HED_MIDDLE_NAME', 'HED_LAST_NAME', 'p_last_name', 'HED_INITIALS', 'p_initials',
  'HED_REPORT_NAME', 'p_report_name', 'HED_GENDER', 'p_sex', 'HED_DATE_OF_BIRTH', 'p_birth_date',
  'HED_MARITAL_STATUS', 'p_marital_status', 'HED_RELIGION', 'p_religion',
  'HED_BLOOD_GROUP', 'p_blood_group', 'HED_NATIONALITY', 'HED_NO_OF_CHILDREN',
  'HED_MOBILE_NO', 'p_mobile', 'HED_HOME_NO', 'p_home_tel', 'HED_OFFICE_NO', 'p_office_tel',
  'HED_PERSONAL_EMAIL', 'p_personal_email', 'HED_COMPANY_EMAIL', 'p_email_address',
  'HED_DISTANCE', 'p_Distance',
  'HED_DESIGNATION_CODE', 'HED_DESIGNATION_NAME', 'p_designation',
  'HDD_DEPARTMENT_CODE', 'HDD_DEPARTMENT_NAME', 'p_department_code',
  'HEC_CATEGORY_CODE', 'HEC_CATEGORY_NAME', 'p_cat_code', 'HED_EMPLOYEE_TYPE', 'HED_OT_TYPE',
  'HGD_GRADE_NAME', 'HGD_GRADE_CODE', 'HGD_MIN_SALARY', 'HGD_MAX_SALARY',
  'HLD_LOCATION_NAME', 'HLD_LOC_NAME', 'HLD_LOC_DESC', 'LocationName', 'locationName', 'HedLocationName',
  'HED_COSTCENTER', 'HedCostCenter', 'CostCenter', 'costCenter', 'p_location_code',
  'HED_GROUP', 'HED_REPORTING_EMPLOYEE_ID', 'HED_REPORTING_EMPLOYEE_NAME',
  'HSD_SHIFT_CODE', 'HSD_SHIFT_NAME', 'HSD_START_TIME', 'HSD_END_TIME',
  'HSD_BREAK_START_TIME', 'HSD_BREAK_END_TIME', 'HSD_BREAK_DURATION_MIN',
  'HSD_GRACE_IN_MIN', 'HSD_GRACE_OUT_MIN', 'HSD_HALF_DAY_HOURS', 'HSD_FULL_DAY_HOURS',
  'HSD_IS_NIGHT_SHIFT', 'HSD_IS_FLEXIBLE_SHIFT', 'HSD_OT_AFTER_MINUTES',
  'HED_DATE_OF_JOINING', 'p_recruitment_date', 'HED_RECRUITMENT_DATE',
  'HED_CONFIRMATION_DATE', 'p_permanent_date', 'HED_PERMANENT_DATE', 'HED_PROMOTED_DATE',
  'HED_RETIREMENT_DATE', 'HED_TERMINATION_DATE', 'HED_TOTAL_EXPERIANCE', 'HED_TOTAL_EXPERIENCE',
  'HED_NOTICE_PERIOD_DAYS', 'HED_OT_RATE',
  'HED_IS_ACTIVE', 'p_inactive_type', 'HED_LEAVING_DATE', 'HED_LEAVING_REASON', 'HED_LEAVING_REMARKS',
  'HED_IS_APPROVED', 'HED_APPROVED_BY', 'HED_APPROVED_DATE', 'HED_IS_SYSTEM_USER',
  'HED_REMARKS', 'p_remarks',
]);

const KEY_LABELS = { IMAGEURL: 'Image URL', NICURL: 'NIC URL', BIRTHCERTURL: 'Birth Certificate URL' };

// HED_JOB_DESCRIPTION_URL → "Job Description URL" (table prefix like HED_ / p_ dropped)
const labelFromKey = (k) => {
  if (KEY_LABELS[k]) return KEY_LABELS[k];
  const words = String(k).replace(/^(p_|H[A-Z]{2}_)/, '').replace(/([a-z])([A-Z])/g, '$1 $2')
    .split(/[_\s]+/).filter(Boolean);
  const text = words
    .map((w) => (/^(url|id|nic|epf|etf|ot|gpa)$/i.test(w) ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()))
    .join(' ');
  return text || String(k);
};

// HED_DESIGNATION_ID, HEB_BANK_ID, … — the matching name is shown instead.
const isIdKey = (k) => /(_ID|Id|ID)$/.test(k) || /^id$/i.test(k);

const isPlain = (v) => v === null || v === undefined || ['string', 'number', 'boolean'].includes(typeof v);

const DATE_LIKE = /^(\d{1,2}\/\d{1,2}\/\d{4}(\s|$)|\d{4}-\d{2}-\d{2}T)/;

/** Raw value as text — dates as yyyy-mm-dd, booleans as Yes / No. */
const anyValue = (v) => {
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  const raw = fmt(v);
  if (/^(true|false)$/i.test(raw)) return /^true$/i.test(raw) ? 'Yes' : 'No';
  return DATE_LIKE.test(raw) ? excelDate(raw) : raw;
};

/** Every plain key found on any record, in first-seen order, minus `skip`. */
function extraKeys(records, skip = new Set()) {
  const order = [];
  const nested = new Set();   // a list or object on any record → detail sheet, not a column
  records.forEach((r) => {
    Object.keys(r || {}).forEach((k) => {
      if (skip.has(k) || isIdKey(k)) return;
      if (!order.includes(k)) order.push(k);
      if (!isPlain(r[k])) nested.add(k);
    });
  });
  return order.filter((k) => !nested.has(k));
}

const extraColumns = (records, skip) =>
  extraKeys(records, skip).map((k) => ({
    label: labelFromKey(k),
    width: Math.min(30, Math.max(12, labelFromKey(k).length + 2)),
    get: (r) => anyValue((r || {})[k]),
  }));


const OWNER_COLUMNS = [
  { label: 'Service No', width: 13, get: (e) => fld(e, 'HED_SERVICE_NO', 'p_ced_service_no') },
  { label: 'Employee',   width: 26, get: (e) => fld(e, 'HED_REPORT_NAME', 'p_report_name') ||
      [fmt(e.HED_FIRST_NAME), fmt(e.HED_LAST_NAME)].filter(Boolean).join(' ') },
];

const DETAIL_SHEETS = [
  {
    name: 'Bank Accounts',
    rowsOf: banksOf,
    columns: [
      { label: 'Bank',           width: 22, get: (b) => fmt(b.HBD_BANK_NAME) },
      { label: 'Branch',         width: 22, get: (b) => fmt(b.HBB_BRANCH_NAME) },
      { label: 'Account Number', width: 20, get: (b) => fmt(b.HEB_ACCOUNT_NUMBER) },
      { label: 'Account Name',   width: 22, get: (b) => fmt(b.HEB_ACCOUNT_NAME) },
      { label: 'Account Type',   width: 13, get: (b) => fmt(b.HEB_ACCOUNT_TYPE) },
      { label: 'Primary',        width: 9,  get: (b) => (truthy(b.HEB_IS_PRIMARY) ? 'Yes' : 'No') },
      { label: 'Approved',       width: 10, get: (b) => (isBankApproved(b) ? 'Yes' : 'No') },
    ],
  },
  {
    name: 'Addresses',
    rowsOf: addressesOf,
    columns: [
      { label: 'Type', width: 12, get: (a) => {
        const t = fmt(a.HEA_ADDRESS_TYPE).toUpperCase();
        if (t === 'P') return 'Permanent';
        if (t === 'C') return 'Current';
        return t;
      } },
      { label: 'Line 1',      width: 24, get: (a) => fmt(a.HEA_ADDRESS_LINE1) },
      { label: 'Line 2',      width: 24, get: (a) => fmt(a.HEA_ADDRESS_LINE2) },
      { label: 'Line 3',      width: 24, get: (a) => fmt(a.HEA_ADDRESS_LINE3) },
      { label: 'City',        width: 18, get: (a) => fmt(a.HEA_CITY) },
      { label: 'Postal Code', width: 12, get: (a) => fmt(a.HEA_POSTAL_CODE) },
      { label: 'Province',       width: 18, get: (a, emp, ctx) => regionOf(a, 'province', ctx) },
      { label: 'District',       width: 18, get: (a, emp, ctx) => regionOf(a, 'district', ctx) },
      { label: 'Electorate',     width: 20, get: (a, emp, ctx) => regionOf(a, 'electorate', ctx) },
      { label: 'Police Station', width: 22, get: (a, emp, ctx) => regionOf(a, 'policeStation', ctx) },
      { label: 'Grama Division', width: 22, get: (a, emp, ctx) => regionOf(a, 'grama', ctx) },
    ],
  },
  {
    name: 'Emergency Contacts',
    rowsOf: contactsOf,
    columns: [
      { label: 'Name',         width: 24, get: (c) => fmt(c.HEC_CONTACT_NAME) },
      { label: 'Relationship', width: 16, get: (c) => fmt(c.HEC_RELATIONSHIP) },
      { label: 'Contact No',   width: 16, get: (c) => fmt(c.HEC_CONTACT_NUMBER) },
      { label: 'Type',         width: 14, get: (c) => fmt(c.HEC_CONTACT_TYPE) },
      { label: 'Address',      width: 34, get: (c) => fmt(c.HEC_ADDRESS) },
    ],
  },
  {
    name: 'Qualifications',
    rowsOf: qualsOf,
    columns: [
      { label: 'Qualification',  width: 28, get: (q) => fmt(q.HEQ_QUALIFICATION_NAME) },
      { label: 'Field of Study', width: 24, get: (q) => fmt(q.HEQ_FIELD_OF_STUDY) },
      { label: 'Institute',      width: 28, get: (q) => fmt(q.HEQ_INSTITUTE) },
      { label: 'Result',         width: 14, get: (q) => fmt(q.HEQ_RESULT) },
      { label: 'Grade',          width: 12, get: (q) => fmt(q.HEQ_GRADE) },
      { label: 'GPA',            width: 10, get: (q) => fmt(q.HEQ_GPA) },
      { label: 'Start Date',     width: 13, get: (q) => excelDate(q.HEQ_START_DATE) },
      { label: 'Completed Date', width: 15, get: (q) => excelDate(q.HEQ_COMPLETED_DATE) },
      { label: 'Certificate No', width: 16, get: (q) => fmt(q.HEQ_CERTIFICATE_NO) },
    ],
  },
  {
    name: 'Documents',
    rowsOf: (e) => listOf(e, 'Documents', '_Documents'),
    columns: [],          // every field the API sends is added below
  },
];

// Keys each detail sheet already shows by name.
const DETAIL_COVERED = {
  'Bank Accounts': ['HBD_BANK_NAME', 'HBB_BRANCH_NAME', 'HEB_ACCOUNT_NUMBER', 'HEB_ACCOUNT_NAME',
    'HEB_ACCOUNT_TYPE', 'HEB_IS_PRIMARY', 'HEB_IS_APPROVED', 'HBD_IS_APPROVED', 'HBB_IS_APPROVED'],
  Addresses: ['HEA_ADDRESS_TYPE', 'HEA_ADDRESS_LINE1', 'HEA_ADDRESS_LINE2', 'HEA_ADDRESS_LINE3',
    'HEA_CITY', 'HEA_POSTAL_CODE', ...REGION_ADDRESS_KEYS],
  'Emergency Contacts': ['HEC_CONTACT_NAME', 'HEC_RELATIONSHIP', 'HEC_CONTACT_NUMBER',
    'HEC_CONTACT_TYPE', 'HEC_ADDRESS'],
  Qualifications: ['HEQ_QUALIFICATION_NAME', 'HEQ_FIELD_OF_STUDY', 'HEQ_INSTITUTE', 'HEQ_RESULT',
    'HEQ_GRADE', 'HEQ_GPA', 'HEQ_START_DATE', 'HEQ_COMPLETED_DATE', 'HEQ_CERTIFICATE_NO'],
};

// List fields that already have a sheet above.
const KNOWN_LIST_KEYS = new Set(['Banks', '_Banks', 'Addresses', '_Addresses', 'Contacts', '_Contacts',
  'Qualifications', '_Qualifications', 'Educations', '_Educations', 'Documents', '_Documents']);

function buildDetailSheet(spec, employees, ctx) {
  const children = [];
  employees.forEach((emp) => spec.rowsOf(emp).forEach((c) => children.push(c)));
  const specColumns = [
    ...spec.columns,
    ...extraColumns(children, new Set(DETAIL_COVERED[spec.name] || [])),
  ];
  const columns = [...OWNER_COLUMNS, ...specColumns];
  const body = [];

  employees.forEach((emp) => {
    spec.rowsOf(emp).forEach((child) => {
      body.push([
        ...OWNER_COLUMNS.map((c) => c.get(emp)),
        ...specColumns.map((c) => c.get(child, emp, ctx)),
      ]);
    });
  });

  // An empty sheet still gets its header row, so the file's shape never varies.
  const ws = XLSX.utils.aoa_to_sheet([columns.map((c) => c.label), ...body]);
  ws['!cols'] = columns.map((c) => ({ wch: c.width || 14 }));
  if (body.length) {
    ws['!autofilter'] = {
      ref: XLSX.utils.encode_range({
        s: { r: 0, c: 0 },
        e: { r: body.length, c: columns.length - 1 },
      }),
    };
  }
  return ws;
}


export function exportEmployeesToExcel({
  employees = [], allEmployees = null, filters = {},
  // Region master lists are optional: pass them to print names, omit them
  // and the columns fall back to the raw codes.
  provinces = [], districts = [], electorates = [], policeStations = [], gramas = [],
} = {}) {
  const ctx = {
    allEmployees: (allEmployees && allEmployees.length) ? allEmployees : employees,
    // Optional: pass region master lists to print names instead of codes.
    provinces, districts, electorates, policeStations, gramas,
  };

  const activeFilters =
    Object.entries(filters)
      .filter(([, v]) => v && v !== 'All')
      .map(([k, v]) => `${k}: ${v}`)
      .join('   |   ') || 'None';

  const activeCount = employees.filter(isActive).length;

  const headerRows = [
    ['EMPLOYEE DETAILS'],
    [`Filters: ${activeFilters}`],
    [`Generated: ${stamp()}`],
    [`Employees: ${employees.length}   (Active: ${activeCount}   Inactive: ${employees.length - activeCount})`],
    [],
  ];

  // Named columns first, then every other field GetEmployees returned.
  const columns = [...EMPLOYEE_EXPORT_COLUMNS, ...extraColumns(employees, COVERED_KEYS)];

  const columnHeader = columns.map((c) => c.label);
  const bodyRows = employees.map((emp) => columns.map((c) => c.get(emp, ctx)));

  const totalsRow = columns.map((c, i) =>
    (i === 0 ? `TOTAL — ${employees.length} employee${employees.length === 1 ? '' : 's'}` : ''));

  const aoa = [...headerRows, columnHeader, ...bodyRows, [], totalsRow];
  const ws = XLSX.utils.aoa_to_sheet(aoa);

  ws['!cols'] = columns.map((c) => ({ wch: c.width || 14 }));

  const lastCol = columns.length - 1;
  ws['!merges'] = headerRows
    .slice(0, 4)
    .map((_, i) => ({ s: { r: i, c: 0 }, e: { r: i, c: lastCol } }));


  ws['!autofilter'] = {
    ref: XLSX.utils.encode_range({
      s: { r: headerRows.length, c: 0 },
      e: { r: headerRows.length + bodyRows.length, c: lastCol },
    }),
  };

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Employees');

  DETAIL_SHEETS.forEach((spec) => {
    XLSX.utils.book_append_sheet(wb, buildDetailSheet(spec, employees, ctx), spec.name);
  });

  // Any other list on the record (one the sheets above don't know) gets a
  // sheet of its own with all of its fields.
  const otherLists = [];
  employees.forEach((emp) => Object.keys(emp || {}).forEach((k) => {
    if (Array.isArray(emp[k]) && !KNOWN_LIST_KEYS.has(k) && !otherLists.includes(k)) otherLists.push(k);
  }));
  otherLists.forEach((k) => {
    const name = labelFromKey(k).slice(0, 31) || k.slice(0, 31);
    if (wb.SheetNames.includes(name)) return;
    XLSX.utils.book_append_sheet(wb,
      buildDetailSheet({ name, rowsOf: (e) => listOf(e, k), columns: [] }, employees, ctx), name);
  });

  const out = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([out], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });

  const d = new Date();
  const filename = `Employee_Details_${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}.xlsx`;

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);

  return filename;
}